import { create } from 'zustand';
import { getUserAvatar } from '../utils/avatar';
/**
 * 带超时的 fetch。
 *
 * 为什么需要：后端一旦不响应（重启中、死锁、网络黑洞），裸 fetch 会一直等，
 * 页面就永远停在「正在加载登录状态...」。加超时后请求会 reject，
 * 由各调用方的 catch 兜底（initializeAuth 会落到登录页）。
 *
 * 注意：api.ts 的 axios 实例自己带 timeout，走 axios 的请求不受影响，
 * 只有本文件这几处裸 fetch 需要它。
 */
const AUTH_TIMEOUT_MS = 15000;
const LOGOUT_TIMEOUT_MS = 5000;

const fetchWithTimeout = async (
  input: string,
  init: RequestInit = {},
  timeoutMs: number = AUTH_TIMEOUT_MS,
): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

interface User {
  id: number;
  username: string;
  email: string;
  role: 'admin' | 'operator';
  avatar?: string;
}

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearAuth: () => void;
  initializeAuth: () => Promise<void>;
  checkAuth: () => boolean;
}

interface ApiUser {
  id?: number;
  username?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  is_admin?: boolean;
  role?: string | null;
}

const mapUser = (data: ApiUser, fallbackName?: string): User => {
  const displayName =
    data.username ||
    fallbackName ||
    data.first_name ||
    data.last_name ||
    'admin';

  return {
    id: data.id ?? 0,
    username: displayName,
    email: data.email || '',
    role: data.is_admin || data.role === 'admin' ? 'admin' : 'operator',
    avatar: getUserAvatar(displayName, '4F46E5'),
  };
};

export const useAuthStore = create<AuthState>((set, get) => ({
  isInitialized: false,
  isAuthenticated: false,
  user: null,

  login: async (username: string, password: string) => {
    try {
      const response = await fetchWithTimeout('/api/v1/auth/admin/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();

      if (data.user_info) {
        set({
          isInitialized: true,
          isAuthenticated: true,
          user: mapUser(data.user_info, username),
        });

        return true;
      }

      return false;
    } catch (error) {
      console.error('登录错误:', error);
      return false;
    }
  },

  clearAuth: () => {
    set({
      isInitialized: true,
      isAuthenticated: false,
      user: null,
    });
  },

  logout: async () => {
    try {
      await fetchWithTimeout('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      }, LOGOUT_TIMEOUT_MS);
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      get().clearAuth();
    }
  },

  initializeAuth: async () => {
    try {
      const response = await fetchWithTimeout('/api/v1/auth/session', {
        credentials: 'include',
      });

      if (!response.ok) {
        set({
          isInitialized: true,
          isAuthenticated: false,
          user: null,
        });
        return;
      }

      const data = await response.json();
      if (!data.authenticated || !data.user) {
        set({
          isInitialized: true,
          isAuthenticated: false,
          user: null,
        });
        return;
      }

      set({
        isInitialized: true,
        isAuthenticated: true,
        user: mapUser(data.user),
      });
    } catch (error) {
      console.error('初始化认证状态失败:', error);
      set({
        isInitialized: true,
        isAuthenticated: false,
        user: null,
      });
    }
  },

  checkAuth: () => {
    return get().isAuthenticated;
  },
}));
